import { trace, context as otelContext } from '@opentelemetry/api';

// ============================================================================
// STRUCTURED LOGGER
// ============================================================================
// Lightweight JSON logger with dev-friendly formatting.
// OTEL trace context (trace_id, span_id) is injected automatically in production logs.

const LogLevel = {
  debug: 0,
  info: 1,
  warn: 2,
  error: 3,
} as const;

type LogLevelName = keyof typeof LogLevel;

// Browser bundles import this module too (e.g. client useConversationEvents).
// `process` is Node-only, so read env via a guarded helper. In the browser we
// fall back to defaults; OTel calls below are no-ops without an SDK, so the
// rest of the module works as-is.
const env: Record<string, string | undefined> = typeof process !== 'undefined' && process.env ? process.env : {};

const currentLevel = LogLevel[(env.LOG_LEVEL as LogLevelName) ?? 'info'] ?? LogLevel.info;
const isDev = (env.NODE_ENV ?? 'development') === 'development';

/**
 * Structured logger that outputs colored text in dev, JSON in production.
 * Automatically serializes Error objects including cause chains (e.g., Drizzle wrapping Postgres errors).
 * Respects LOG_LEVEL environment variable.
 *
 * Usage:
 *   logger.info('Server started', { port: 4100 })
 *   logger.error('Failed to save', error)
 *   logger.error('Failed to save', error, { symbol: 'BTCUSDT' })
 *   logger.warn('Slow query', { duration: 1200 })
 */
export const logger = {
  debug(message: string, attributes?: unknown) {
    if (currentLevel > LogLevel.debug) return;
    log('debug', message, attributesOf(attributes));
  },

  info(message: string, attributes?: unknown) {
    if (currentLevel > LogLevel.info) return;
    log('info', message, attributesOf(attributes));
  },

  warn(message: string, attributes?: unknown) {
    if (currentLevel > LogLevel.warn) return;
    log('warn', message, attributesOf(attributes));
  },

  /**
   * Error level logging with flexible signatures:
   *   error(message)
   *   error(message, attributes)
   *   error(message, error)
   *   error(message, error, attributes)
   */
  error(message: string, errorOrAttrs?: unknown, maybeAttrs?: Record<string, unknown>) {
    const isError = errorOrAttrs instanceof Error;
    const isAttrs = !isError && errorOrAttrs !== null && typeof errorOrAttrs === 'object';
    const error = isError ? errorOrAttrs : !isAttrs && errorOrAttrs != null ? errorOrAttrs : undefined;
    const attributes = maybeAttrs ?? (isAttrs ? (errorOrAttrs as Record<string, unknown>) : {});

    const errorAttrs =
      error instanceof Error
        ? exceptionAttributes(error)
        : error != null
          ? { 'exception.message': String(error), 'exception.type': typeof error }
          : {};

    log('error', message, { ...attributes, ...errorAttrs });
  },
};

/** A Node system error's code (`ECONNREFUSED`, `ETIMEDOUT`, …), when it carries one. */
function codeOf(error: Error): string | undefined {
  const code = (error as { code?: unknown }).code;
  return typeof code === 'string' ? code : undefined;
}

/** An error as log attributes: its message, type, code and stack, and its cause's. */
function exceptionAttributes(error: Error): Record<string, unknown> {
  const code = codeOf(error);
  return {
    'exception.message': error.message,
    'exception.type': error.name,
    ...(code ? { 'exception.code': code } : {}),
    'exception.stacktrace': error.stack,
    // Drizzle (and many libs) wrap the real driver error as error.cause, and `fetch` wraps the socket's
    // ("fetch failed" ← ECONNREFUSED). Include it so the log names what actually failed.
    ...(error.cause instanceof Error
      ? {
          'exception.cause.message': error.cause.message,
          'exception.cause.type': error.cause.name,
          ...(codeOf(error.cause) ? { 'exception.cause.code': codeOf(error.cause) } : {}),
          'exception.cause.stacktrace': error.cause.stack,
        }
      : error.cause != null
        ? {
            'exception.cause': String(error.cause),
          }
        : {}),
  };
}

/**
 * The attributes of a debug, info or warn line. An Error passed as them (`logger.warn('poll failed', err)`, 137 call
 * sites) is serialised as `error()` serialises one: spread as a plain object it had no enumerable fields, so the line
 * carried no reason at all — the worker box's 14:36–14:48 UTC "control poll failed" / "heartbeat failed" run on
 * 2026-10-28 could only be explained from the tunnel's log (the Mac's server was not listening on 4100).
 */
function attributesOf(attributes: unknown): Record<string, unknown> {
  if (attributes instanceof Error) return exceptionAttributes(attributes);
  return typeof attributes === 'object' && attributes !== null ? (attributes as Record<string, unknown>) : {};
}

export type Logger = typeof logger;

// ============================================================================
// Internal log function
// ============================================================================

function log(level: LogLevelName, message: string, attributes: Record<string, unknown>) {
  if (isDev) {
    formatDevLog(level, message, attributes);
  } else {
    // Production: structured JSON to stdout/stderr with OTEL trace context
    const spanContext = trace.getSpan(otelContext.active())?.spanContext();
    const traceFields = spanContext?.traceId ? { trace_id: spanContext.traceId, span_id: spanContext.spanId } : {};
    const output = JSON.stringify({
      timestamp: new Date().toISOString(),
      level,
      message,
      ...traceFields,
      ...(Object.keys(attributes).length > 0 ? { attributes } : {}),
    });

    switch (level) {
      case 'error':
        console.error(output);
        break;
      case 'warn':
        console.warn(output);
        break;
      default:
        console.log(output);
    }
  }
}

// ============================================================================
// Dev-friendly formatting
// ============================================================================

const colors = {
  debug: '\x1b[36m', // Cyan
  info: '\x1b[32m', // Green
  warn: '\x1b[33m', // Yellow
  error: '\x1b[31m', // Red
  reset: '\x1b[0m',
  dim: '\x1b[2m',
  bold: '\x1b[1m',
};

function formatDevLog(level: LogLevelName, message: string, attributes: Record<string, unknown>) {
  const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false });
  const levelColor = colors[level];
  const levelUpper = level.toUpperCase().padEnd(5);

  // Filter out exception attributes for inline display — they're printed separately
  const displayAttrs: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(attributes)) {
    if (!k.startsWith('exception.')) displayAttrs[k] = v;
  }
  const attrStr =
    Object.keys(displayAttrs).length > 0 ? ` ${colors.dim}${JSON.stringify(displayAttrs)}${colors.reset}` : '';

  console.log(
    `${colors.dim}${timestamp}${colors.reset} ` + `${levelColor}${levelUpper}${colors.reset} ` + `${message}${attrStr}`,
  );

  // Print error details below the message
  if (level === 'error') {
    if (attributes['exception.stacktrace']) {
      console.error(String(attributes['exception.stacktrace']));
    } else if (attributes['exception.message']) {
      console.error(
        `${colors.error}${attributes['exception.type']}: ${attributes['exception.message']}${colors.reset}`,
      );
    }
    if (attributes['exception.cause.message']) {
      console.error(
        `\n${colors.dim}Caused by: ${attributes['exception.cause.type']}: ${attributes['exception.cause.message']}${colors.reset}`,
      );
      if (attributes['exception.cause.stacktrace']) {
        console.error(String(attributes['exception.cause.stacktrace']));
      }
    }
  }
}

// ============================================================================
// Convenience helpers (backward-compatible with existing call sites)
// ============================================================================

/**
 * Log an error with a tag prefix and structured context.
 *
 * Usage:
 *   logError('[DataFeed]', 'Failed to save candles', error, { symbol: 'BTCUSDT' });
 */
export function logError(tag: string, message: string, error: unknown, attrs?: Record<string, unknown>): void {
  logger.error(`${tag} ${message}`, error instanceof Error ? error : new Error(String(error)), attrs);
}

/**
 * Log a warning with a tag prefix and structured context.
 */
export function logWarn(tag: string, message: string, error?: unknown, attrs?: Record<string, unknown>): void {
  if (error) {
    logger.warn(`${tag} ${message}`, {
      ...attrs,
      'exception.message': error instanceof Error ? error.message : String(error),
    });
  } else {
    logger.warn(`${tag} ${message}`, attrs);
  }
}
